/*
This module is responsible for computing treatment effects using pooling,
association measures, adjustments, etc. */
import * as lodash from 'lodash'
const { groupBy, sortBy } = lodash.uniqueId ? lodash : lodash.default

let analysis
function getPooledOutcomeValue(outcome, groups, replicateR?, continuityCorrection?, continuityCorrectionSetting?) {
	let totalValue = 0
	let totalN = 0
	let totalSD = 0
	let cells = outcome?.cells
	let isBinary = outcome.type == 'Binary' || outcome.dataType == 'Percentage'
	let totalPersonTime = 0
	let transforms
	for (let group of groups) {
		let cell = cells && group && cells.find(cell => cell.group == group.Id)
		if (!cell)
			continue
		let N = cell?.N || group.N
		let number = cell.number
		let sd = cell.sd
		if (!N)
			return { number, sd } // can't pool
		if (cell.calculated) {
			for (let target in cell.calculated)
				(transforms || (transforms = [])).push({
					arm: group.analyzeAs,
					transform: (target == 'number' ? '' : target + ' = ') + cell.calculated[target]
				})
		}
		if (N && isBinary && cell.count > -1)
			number = cell.count / N * 100
		let { adjustment, transform } = getAdjustedEffect(group, analysis)
		if (adjustment && !isNaN(number) && !(isBinary && number == 0)) {
			let originalValue = number
			number = isBinary ?
				number * Math.exp(adjustment) :
				(number + adjustment);
			(transforms || (transforms = [])).push({
				arm: group.analyzeAs,
				transform: (isBinary ?
					roundNum(originalValue) + ' x exp(' + transform + ') = ' + roundNum(number) :
					(roundNum(originalValue) + ' + ' + transform + ' = ' + roundNum(number))) + ' (covariate adjustment)'
			})
		}
		totalSD = totalN ?
			Math.sqrt((totalSD * totalSD * (totalN - 1) + sd * sd * (N - 1)) / (totalN + N - 2)) :
			sd
		totalN += N
		totalValue += number * N
		totalPersonTime += cell.totalPersonTime

		if (sd == null && !isBinary && outcome.type != 'Incidence') {
			(transforms || (transforms = [])).push({
				arm: group.analyzeAs,
				transform: 'Variance will be estimated based on other included studies when analysis is run'
			})
		}
		if (number == null)
			return {}
	}
	let se = totalSD / Math.sqrt(totalN)
	let number = totalValue / totalN
	let N = totalN
	if (replicateR && isBinary) {
		let ratio, count
		if (continuityCorrection) {
			count = Math.round(number * totalN / 100) + (continuityCorrection ? continuityCorrectionSetting : 0)
			N = continuityCorrection ? totalN + continuityCorrectionSetting * 2 : totalN
		} else {
			count = Math.round(number * totalN / 100)
		}
		ratio = count / N
		number = ratio * 100
		totalSD = Math.sqrt((1 - ratio) * (1 - ratio) * ratio +
			(ratio * ratio) * (1 - ratio))
		se = Math.exp(Math.sqrt(1 / count - 1 / N)) * number
	} else if (outcome.type == 'Incidence') {
		totalSD = number
		se = Math.sqrt(1 / (number * totalPersonTime))
	}
	return { number, sd: totalSD, se, N, transforms }
}
export function computeEffect({ interventionGroups, controlGroups, outcome, analysis: appliedAnalysis, groups }) {
	if (!outcome)
		return {}
	if (!interventionGroups || interventionGroups.length < 1) {
		if (!controlGroups || controlGroups.length < 1)
			return {}
		// swap them so we can at least compute the baseline cohort values from the control
		interventionGroups = controlGroups
		controlGroups = null
	}
	analysis = appliedAnalysis
	let settings = analysis.settings || {}
	let isBinary = outcome.type == 'Binary' || outcome.dataType == 'Percentage'
	let isIncidence = outcome.type == 'Incidence'
	let isRatio = !settings.useSMD && (isIncidence ||
		isBinary && (settings.measureBin == 'OR' || settings.measureBin == 'RR' || !settings.measureBin))
	let isOdds = isBinary && settings.measureBin == 'OR'
	let continuityApplied = false
	if (isRatio) {
		let arms = groupBy(groups, 'analyzeAs')
		for (let arm in arms) {
			if (arm != 'undefined') {
				let { number } = getPooledOutcomeValue(outcome, arms[arm], true)
				if (number === 0 || number === 100) {
					continuityApplied = true
					break
				}
			}
		}
	}
	let { number: interventionValue, sd: interventionSD, N: interventionN, transforms } = getPooledOutcomeValue(outcome, interventionGroups, true)
	if (!controlGroups || controlGroups.length < 1) {
		let value = interventionValue
		if (isOdds)
			value = value / (100 - value)
		return { effect: value, transforms }
	}
	let { number: controlValue, sd: controlSD, N: controlN, transforms: controltransforms } = getPooledOutcomeValue(outcome, controlGroups, true)
	let comparativeEffect
	if (interventionValue == null && interventionGroups[0]) {
		let cell = outcome?.cells.find(cell => cell.group == interventionGroups[0].Id)
		let controlCell = controlGroups[0] && outcome?.cells.find(cell => cell.group == controlGroups[0].Id)
		let am = controlCell && cell?.AMs.find(am => am.comparison == controlCell.Id)
		if (am) {
			return { effect: am.number, isComparative: true, transforms }
		}
		return {}
	}
	if (isRatio) {
		if (continuityApplied) {
			if (settings.continuityCorrection == 0)
				return []
			// continuity correction
			if (analysis.analysisType != 'bayesian') {
				let continuityCorrectionSetting = settings.continuityCorrection ?? 0.5
				let intervention = getPooledOutcomeValue(outcome, interventionGroups, true, true, continuityCorrectionSetting)
				interventionValue = intervention.number
				transforms = intervention.transforms
				let control = getPooledOutcomeValue(outcome, controlGroups, true, true, continuityCorrectionSetting)
				controlValue = control.number
				controltransforms = control.transforms
			}
		}
		comparativeEffect = isOdds
			? interventionValue / (100 - interventionValue) / (controlValue / (100 - controlValue)) // odds
			: interventionValue / controlValue // risk
	} else {
		comparativeEffect = interventionValue - controlValue
	}
	if (settings.useSMD) {
		let directionality = typeof outcome.isPositive == 'boolean' ? outcome.isPositive : outcome.suggestedPositive
		let sd = Math.sqrt((interventionSD * interventionSD * interventionN + controlSD * controlSD * controlN) / (interventionN + controlN))
		if (isBinary)
			comparativeEffect /= 100 // convert back to ratio
		comparativeEffect = comparativeEffect / sd
		if (!directionality)
			comparativeEffect = -comparativeEffect
	}
	if (controltransforms)
		(transforms || (transforms = [])).push(...controltransforms)
	if (transforms) {
		let transformByText = new Map()
		transforms = transforms.filter(transform => {
			if (transformByText.has(transform.transform)) {
				transformByText.get(transform.transform).arm += ', '+ transform.arm
			} else {
				transformByText.set(transform.transform, transform)
				return true
			}
		})
	}
	let measureName = outcomeMeasureNameForSettings(outcome, settings, settings.analysisType)
	measureName = fullMeasureName[measureName] || measureName
	return { effect: comparativeEffect, continuityApplied, isComparative: true, transforms, description: measureName + ' of ' + interventionGroups[0].analyzeAs + ' vs ' + controlGroups[0]?.analyzeAs }
}

export function estimateValuesFromAMs(outcome, minMax) {
	let hasGroupData, hasAMRatio, hasAMDiffs, cells = outcome.cells || []
	for (let cell of cells) {
		if (!isNaN(cell.number) || cell.count > -1) {
			hasGroupData = true
			break
		} else if (cell.AMs) {
			if (cell.AMs.some(am => am.AMType.includes('Ratio')))
				hasAMRatio = true
			if (cell.AMs.some(am => am.AMType.includes('Difference')))
				hasAMDiffs = true
		}
	}
	
	if (!hasGroupData) {
		let estimatedValues = new Map()
		if (hasAMRatio || hasAMDiffs) {
			for (let seedCell of cells) {
				if (estimatedValues.has(seedCell.Id) || !seedCell.AMs)
					continue
				estimatedValues.set(seedCell.Id, hasAMRatio ? 1 : 0)
				let hasChanges = true
				while(hasChanges) {
					hasChanges = false
					for (let cell of cells) {
						if (cell.AMs && estimatedValues.has(cell.Id)) {
							for (let am of cell.AMs) {
								if (!estimatedValues.has(am.comparison)) {
									hasChanges = true
									let leftValue = estimatedValues.get(cell.Id)
									estimatedValues.set(am.comparison, hasAMRatio ?
										leftValue / am.number : leftValue - am.number)
								}
							}
						}
					}
				}
			}
			if (hasAMRatio && minMax) {
				let max = Math.max(...estimatedValues.values())
				let adjustment = (minMax.max * 0.8) / max
				for (let cell of cells) {
					cell.estimatedFromAM = estimatedValues.get(cell.Id) * adjustment
				}
			} else if (hasAMDiffs && minMax) {
				let sortedValues = sortBy(Array.from(estimatedValues.values()))
				let median = (sortedValues[sortedValues.length >> 1] + sortedValues[(sortedValues.length - 1) >> 1]) / 2
				let adjustment = (minMax.max + minMax.min) / 2 - median
				for (let cell of cells) {
					cell.estimatedFromAM = estimatedValues.get(cell.Id) + adjustment
				}
			}
		}
	}
}

export function getAdjustedEffect(group, { adjustments, demographics, groupAnalyzeAs }) {
	if (!adjustments || !demographics)
		return {}
	let { age, male } = demographics
	let adjustment = 0
	let transforms = []
	for (let { predictor, m, mean, treatment } of adjustments) {
		let groupSetting = groupAnalyzeAs?.find(groupSetting => groupSetting.groupId == group.Id)
		let analyzeAs
		if (groupSetting)
			// manual assignment of this group to this label
		 	analyzeAs = groupSetting.analyzeLabel
		 else
		 	analyzeAs = group.analyzeAs
		if (analyzeAs != treatment)
			continue
		if (predictor == 'age') {
			if (group.age > - 1) {
				let groupAge = (age || mean) - group.age
				adjustment += groupAge * m
				transforms.push(roundNum(groupAge) + ' x ' + roundNum(m))
			}
		}
		if (predictor == 'gender') {
			if (group.male > - 1) {
				let groupMale = (male || mean) - group.male
				adjustment += groupMale * m
				transforms.push(roundNum(groupMale) + ' x ' + roundNum(m))
			}
		}
	}
	return {
		adjustment,
		transform: transforms.join(' + '),
	}
}

export function isAnalyzable(cell, row) {
	if (row.type == 'Binary' && (cell.number > 100 || cell.number < 0))
		return 0.05
	let hasNumber = typeof cell.number == 'number' && !isNaN(cell.number)
	let hasVariance = row.type == 'Binary' || row.type == 'Incidence' || row.type == 'Count' || (cell && (cell.sd || cell.se || cell.AMs))
	return hasNumber ? hasVariance ? 1 : 0.95 : cell.AMs ? 0.97 : 0
}
export const fullMeasureName = {
	'(IRR)': 'Incidence risk ratio',
	'(RR)': 'Risk ratio',
	'(OR)': 'Odds ratio',
	'(RD)': 'Risk difference',
	'Effect (SMD)': 'Standardized mean difference',
	'(MN)': 'Mean difference',
}
export const outcomeMeasureNameForSettings = (outcome, settings, analysisType, fullLabel?) => {
	if (settings.useSMD && analysisType != 'bayesian' && analysisType != 'cohort')
		return 'Effect (SMD)'
	return outcome.type == 'Binary'
		? 
				analysisType == 'cohort'
					? 'Measure' + (settings.measureBin == 'OR' ? ' (Odds)' : '')
					: (fullLabel ? 'Effect ' : '') + '(' + 
						(settings.measureBin == 'HR' && analysisType != 'bayesian' ? 'RR' : settings.measureBin || 'RR') + ')'
		
		: analysisType == 'cohort'
		? outcome.type == 'Incidence'
			? (fullLabel ? 'Measure ' : '') + '(IR)'
			: (fullLabel ? 'Measure ' : '') + ' (MN)'
		: (fullLabel ? 'Effect ' : '') +
			'(' + (outcome.type == 'Incidence' ?
				settings.measureRate || 'IRR' : settings.measureCont || 'MD') + ')'
}

const roundNum = x =>
	+x.toFixed(Math.min(Math.max(-Math.floor(Math.log10(Math.abs(x))) + 2, 0), 8))
